import { NavLink } from 'react-router-dom';
import { 
  Settings, 
  Users, 
  Shield, 
  Calendar, 
  Clock, 
  MessageSquare, 
  Plane, 
  DollarSign, 
  Sparkles,
  CreditCard,
  MapPin
} from 'lucide-react';
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from '@/components/ui/sidebar';
import { useUserPermissions } from '@/hooks/permissions';
import { PermissionKey, PERMISSION_KEYS } from '@/types/roles';

// Business settings menu item structure
interface BusinessSettingsMenuItem {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  url: string;
  description?: string;
  requiredPermissions: PermissionKey[];
}

/** Check if user has ANY of the given permissions */
const hasAny = (permissions: PermissionKey[], keys: PermissionKey[]): boolean =>
  keys.some(k => permissions.includes(k));

// Business settings menu items — permissions mirror ProtectedRoute guards in App.tsx
const businessSettingsItems: BusinessSettingsMenuItem[] = [
  {
    id: 'general',
    title: 'General',
    icon: Settings,
    url: '/business-settings/general',
    description: 'Basic company information and settings',
    requiredPermissions: [PERMISSION_KEYS.SYSTEM_CONFIG_VIEW, PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE],
  },
  {
    id: 'people',
    title: 'People',
    icon: Users,
    url: '/business-settings/people',
    description: 'Employee management and organization',
    requiredPermissions: [PERMISSION_KEYS.MANAGE_EMPLOYEE_DETAILS],
  },
  {
    id: 'access-permissions',
    title: 'Access & Permissions',
    icon: Shield,
    url: '/business-settings/access-permissions',
    description: 'User access and security settings',
    requiredPermissions: [PERMISSION_KEYS.CAN_MANAGE_ROLES],
  },
  {
    id: 'scheduling',
    title: 'Scheduling',
    icon: Calendar,
    url: '/business-settings/scheduling',
    description: 'Scheduling rules and workforce planning',
    requiredPermissions: [PERMISSION_KEYS.SYSTEM_CONFIG_VIEW, PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE],
  },
  {
    id: 'time-attendance',
    title: 'Time & Attendance',
    icon: Clock,
    url: '/business-settings/time-attendance',
    description: 'Time tracking and attendance policies',
    requiredPermissions: [PERMISSION_KEYS.SYSTEM_CONFIG_VIEW, PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE],
  },
  {
    id: 'geolocation',
    title: 'Geolocation',
    icon: MapPin,
    url: '/business-settings/geolocation',
    description: 'Location-based time tracking',
    requiredPermissions: [PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE],
  },
  {
    id: 'communications',
    title: 'Communications',
    icon: MessageSquare,
    url: '/business-settings/communications',
    description: 'Notifications and messaging settings',
    requiredPermissions: [PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE],
  },
  {
    id: 'leave-policies',
    title: 'Leave Policies',
    icon: Plane,
    url: '/business-settings/leave-policies',
    description: 'Leave types and approval workflows',
    requiredPermissions: [PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE],
  },
  {
    id: 'pay',
    title: 'Pay',
    icon: DollarSign,
    url: '/business-settings/pay',
    description: 'Payroll and compensation settings',
    requiredPermissions: [PERMISSION_KEYS.MANAGE_EMPLOYEE_PAY],
  },
  {
    id: 'holidays',
    title: 'Holidays',
    icon: Calendar,
    url: '/business-settings/holidays',
    description: 'Company holidays and public holiday calendars',
    requiredPermissions: [PERMISSION_KEYS.SYSTEM_CONFIG_VIEW, PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE],
  },
  {
    id: 'billing',
    title: 'Billing',
    icon: CreditCard,
    url: '/business-settings/billing',
    description: 'Subscription and billing management',
    requiredPermissions: [PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE],
  },
  {
    id: 'upcoming-features',
    title: 'Upcoming Features',
    icon: Sparkles,
    url: '/business-settings/upcoming-features',
    description: 'Preview and configure beta features',
    requiredPermissions: [PERMISSION_KEYS.SYSTEM_CONFIG_VIEW, PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE],
  }
];

/**
 * Returns the count of visible business settings items for the current user.
 * Used by AppSidebar to hide the toggle when no items are accessible.
 */
export function useVisibleBusinessSettingsCount(): number {
  const { permissions } = useUserPermissions();
  return businessSettingsItems.filter(item =>
    hasAny(permissions, item.requiredPermissions)
  ).length;
}

/**
 * Business Settings submenu component
 * Renders only the submenu items the user has permission to access
 */
export function BusinessSettingsMenu() {
  const { setOpenMobile } = useSidebar();
  const { permissions } = useUserPermissions();

  const visibleItems = businessSettingsItems.filter(item =>
    hasAny(permissions, item.requiredPermissions)
  );

  const getNavClassName = ({ isActive }: { isActive: boolean }) =>
    isActive 
      ? 'bg-accent text-accent-foreground font-medium' 
      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50';

  const handleNavClick = () => {
    setOpenMobile(false);
  };

  if (visibleItems.length === 0) return null;

  return (
    <SidebarMenu>
      {visibleItems.map((item) => (
        <SidebarMenuItem key={item.id}>
          <SidebarMenuButton asChild>
            <NavLink 
              to={item.url}
              className={getNavClassName}
              onClick={handleNavClick}
            >
              <item.icon className="h-4 w-4" />
              <span>{item.title}</span>
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );
}
